import { MouseEvent, ReactElement, ReactNode, useCallback, useMemo } from 'react';

import { Box, Button, Paragraph, rem } from '@vimeo/bokeh';
import { CloseX } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';

import { createDomName } from '../../../../utils/dom/identifying';

interface IQuestionListNotificationProps extends IBaseDomElementProps {
  content: ReactNode;
  placement: string;
  icon?: ReactElement;
  onClick?: () => void;
  onDismiss?: () => void;
}

export function QuestionListNotification({
  id = createDomName('question-list-notification'),
  className = createDomName('question-list-notification'),
  content,
  placement,
  icon,
  onDismiss,
  onClick,
}: IQuestionListNotificationProps) {
  const onDismissClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();

      if (onDismiss) {
        onDismiss();
      }
    },
    [onDismiss],
  );

  const onConfirmClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();

      if (onClick) {
        onClick();
      }
    },
    [onClick],
  );

  const iconProps = useMemo(() => {
    if (icon) {
      return {
        leftIcon: icon,
      };
    } else if (onDismiss) {
      return {
        rightIcon: <CloseX onClick={onDismissClick} />,
      };
    } else {
      return {};
    }
  }, [icon, onDismiss, onDismissClick]);

  return (
    <Box
      id={id}
      className={className}
      position={'absolute'}
      alignSelf={'center'}
      opacity={1}
      sx={placement === 'bottom' ? { bottom: rem(20) } : { top: rem(60) }}
    >
      <Button
        id={createDomName(className, 'button')}
        className={createDomName(className, 'button')}
        variant={'primary'}
        size={'xs'}
        {...iconProps}
        padding={`0 ${rem(8)}`}
        onClick={onConfirmClick}
      >
        <Paragraph
          id={createDomName(className, 'text')}
          className={createDomName(className, 'text')}
          size={'md'}
          as={'h2'}
        >
          {content}
        </Paragraph>
      </Button>
    </Box>
  );
}
